/**
 * SavingsGoals — multiple savings buckets with per-goal progress.
 */
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { NumberField, TextField, SelectField, ProgressBar, Money, StatCell } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { Button } from "@/components/ui/button";

const CATEGORIES = [
  ["general", "General"], ["emergency", "Emergency (backup)"],
  ["vacation", "Vacation"], ["car", "Car"], ["home", "Home"],
  ["education", "Education"], ["wedding", "Wedding"], ["custom", "Custom"],
];

export default function SavingsGoals() {
  const { plan, summary, patch } = useFinancialPlan();
  const savings = plan?.savings || [];
  const [busy, setBusy] = useState(false);

  const upsert = async (updated) => {
    setBusy(true);
    try { await patch({ savings: updated }); } finally { setBusy(false); }
  };
  const addSavings = () => upsert([...savings, {
    id: `s_${Date.now()}`, name: "New savings goal", category: "general",
    current_balance: 0, goal: 0, monthly_contribution: 0,
  }]);
  const remove = (id) => upsert(savings.filter((s) => s.id !== id));
  const patchOne = (id, changes) => upsert(savings.map((s) => s.id === id ? { ...s, ...changes } : s));

  return (
    <div className="space-y-4" data-testid="savings-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <StatCell label="Total savings" value={<Money amount={summary?.total_savings} currency={plan?.currency} />} testId="savings-total" />
        <StatCell label="Monthly contributions" value={<Money amount={summary?.monthly_savings} currency={plan?.currency} />} testId="savings-monthly" />
        <StatCell label="Overall savings rate" value={`${summary?.savings_rate_pct ?? 0}%`} sub="of gross income" testId="savings-rate" tone={summary?.savings_rate_pct >= 20 ? "good" : summary?.savings_rate_pct < 10 ? "warn" : "default"} />
      </div>

      {savings.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
          No savings goals yet. Add one to track progress against a target.
        </div>
      )}

      {savings.map((s) => {
        const pct = s.goal ? Math.min(100, (s.current_balance / s.goal) * 100) : 0;
        return (
          <div key={s.id} className="border border-border rounded-lg p-4 space-y-3" data-testid={`savings-row-${s.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField label="Name" value={s.name} onChange={(v) => patchOne(s.id, { name: v })} testId={`savings-name-${s.id}`} />
              <SelectField label="Category" value={s.category} onChange={(v) => patchOne(s.id, { category: v })} options={CATEGORIES} testId={`savings-cat-${s.id}`} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <NumberField label="Current" value={s.current_balance} onChange={(v) => patchOne(s.id, { current_balance: v })} prefix={plan?.currency} testId={`savings-balance-${s.id}`} />
              <NumberField label="Goal" value={s.goal} onChange={(v) => patchOne(s.id, { goal: v })} prefix={plan?.currency} testId={`savings-goal-${s.id}`} />
              <NumberField label="Monthly" value={s.monthly_contribution} onChange={(v) => patchOne(s.id, { monthly_contribution: v })} prefix={plan?.currency} testId={`savings-monthly-${s.id}`} />
            </div>
            {s.goal > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{Math.round(pct)}% of goal</div>
                <ProgressBar value={pct} tone={pct >= 100 ? "good" : "primary"} />
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={() => remove(s.id)} className="text-xs text-destructive hover:underline flex items-center gap-1" data-testid={`savings-remove-${s.id}`}>
                <Trash2 size={12} /> Remove
              </button>
            </div>
          </div>
        );
      })}

      <Button size="sm" variant="outline" onClick={addSavings} disabled={busy} data-testid="savings-add-btn">
        <Plus size={13} className="mr-1" /> Add savings goal
      </Button>
    </div>
  );
}
